import { resolveRateUnit, lineReconcile } from '../reports/taxRate';

// ═══ A NOTICE'S RATE TABLE → jurisdiction_rates' SHAPE ═══
//
// A Florida TRIM prints, per taxing authority, a taxable value and THREE rate/tax pairs: last year, if no
// budget change is made, and if the proposed change is made. The bill will carry the proposed one.
//
// ⛔ AND ARITHMETIC CANNOT TELL THEM APART. All three pairs reconcile against the SAME taxable value —
// 497,360 × 5.0254/1000 = 2,499.43 for the proposed pair, and the no-change pair reconciles just as exactly.
// So a model that reads the wrong column returns a triple that verifies perfectly. Reconciliation catches a
// unit error and a transcription error; it is BLIND to a wrong-scenario error, which is the one that produces
// a plausible rate that is simply not the one the bill will use.
//
// ⛔ SO THE MODEL IS NOT ASKED TO CHOOSE. It transcribes all three scenarios LABELLED and this selects. That
// is the same principle DocumentScanner:41 applies to arithmetic — "THE MODEL TRANSCRIBES LINES; JS DOES THE
// ADDITION" — after prose instructions failed to stop it returning the general levy as an ad-valorem total.
// It also records which column was used BY CONSTRUCTION: the key name is the provenance.
const SCENARIOS = ['proposed', 'no_change', 'last_year'];

const n = (v) => {
  if (v == null || v === '') return null;
  const x = typeof v === 'number' ? v : parseFloat(String(v).replace(/[$,%\s]/g, ''));
  return Number.isFinite(x) ? x : null;
};
const pair = (line, key) => {
  const sc = line && line[key];
  if (!sc || typeof sc !== 'object') return null;
  const rate = n(sc.tax_rate), tax = n(sc.tax);
  return rate == null ? null : { rate, tax };
};

export function flattenNoticeRateLines(raw, opts = {}) {
  const { scenario = 'proposed' } = opts;
  const list = Array.isArray(raw) ? raw.filter((l) => l && typeof l === 'object') : [];
  const none = (note) => ({ lines: null, source: null, note });
  if (!list.length) return none('The notice extraction returned no rate table.');

  const usable = list.filter((l) => n(l.taxable_value) != null && pair(l, scenario));
  if (!usable.length) return none(`No authority on the notice carried a ${scenario.replace('_', ' ')} rate with a taxable value.`);

  // ⛔ THE SCENARIO-COLLAPSE GUARD, which is the only check that can catch a wrong column. If the model has
  // folded the three pairs into one it will report the same rate under every label — so at least two labelled
  // scenarios must be present AND they must actually differ somewhere. Capture nothing otherwise: a
  // plausible rate from the wrong column is worse than a blank, and nothing downstream could detect it.
  //
  // ⛔ ONLY `proposed` COMING BACK IS ALSO A REFUSAL, and that is deliberate even though it discards data that
  // might be right. A single labelled column is indistinguishable from a model that read one column and
  // labelled it from the instruction rather than the page.
  const others = SCENARIOS.filter((k) => k !== scenario);
  const present = others.filter((k) => usable.some((l) => pair(l, k)));
  if (!present.length) {
    return none(`The notice returned only a ${scenario.replace('_', ' ')} rate, with no other scenario to compare it against — so it cannot be confirmed as the right column. Enter the rates from the notice.`);
  }
  const differsSomewhere = present.some((k) => usable.some((l) => {
    const a = pair(l, scenario), b = pair(l, k);
    return a && b && a.rate !== b.rate;
  }));
  if (!differsSomewhere) {
    return none(`Every scenario on the notice came back with the same rate, so the three columns were not told apart. Enter the rates from the notice.`);
  }

  // jurisdiction_rates' shape, so one resolver and one suggestion path serve both sources.
  const lines = usable.map((l) => {
    const p = pair(l, scenario);
    return { name: l.name || null, tax_rate: p.rate, taxable_value: n(l.taxable_value), amount: p.tax };
  });

  // ⛔ FLAGGED, NEVER DROPPED. A line that does not reconcile stays in the array carrying _reconciled:false.
  // Dropping it is the EDGEWATER I&S failure — 7452220A0030 lost one line to extraction, every remaining line
  // was internally perfect, and nothing noticed for weeks. Consumers refuse to total an array with a flag
  // rather than summing around it.
  // ⛔ FLAG AGAINST THE BETTER-FITTING DIVISOR, NOT ONLY A CONFIDENT ONE. resolveRateUnit reports
  // confident:false unless EVERY line reconciles at one divisor — so a single broken line made the whole unit
  // unresolvable and then no line could be flagged individually, which defeats the flag-never-drop rule at
  // exactly the moment it matters. `best` (fewest failures, percent breaking the tie) exists for this: it is
  // what check-tax-bills' per-line check reports against. The UNIT NAME is still only claimed when confident,
  // so an unresolved unit never silently sets the toggle.
  const unit = resolveRateUnit(lines);
  const divisor = unit.confident ? unit.divisor : unit.best.divisor;
  const flagged = lines.map((l) => {
    const r = lineReconcile(l, divisor);
    return { ...l, _reconciled: r.ok === null ? null : r.ok };
  });
  const bad = flagged.filter((l) => l._reconciled === false).length;

  return {
    lines: flagged,
    source: `notice:${scenario}`,
    note: !unit.confident
      ? `Captured ${flagged.length} rate lines, but millage could not be told from percent (${unit.reason})`
        + `${bad ? `; ${bad} do not reconcile and are marked` : ''} — check the unit against the notice.`
      : bad
        ? `Captured ${flagged.length} rate lines; ${bad} do not reconcile against the notice's own tax figures and are marked.`
        : null,
    unit: unit.confident ? unit.unit : null,
    dropped: list.length - usable.length,
  };
}

// ═══ HOW MANY TAXABLE BASES DOES THIS NOTICE STATE? ═══
//
// ⛔ THIS REPLACES A CLAIM NOBODY EVER CHECKED. DocumentScanner showed "AI detected all jurisdictions have
// identical values — County column only required" whenever `sameForAll && jurisdictions.length === 1`. But
// sameForAll is a useState(true) that nothing ever assigns from extraction — it is the AUTO-FILL CHECKBOX's
// own flag, labelled "Auto-fill all jurisdictions with same values as primary column" — and the scan always
// seeds exactly one County column. So the banner fired on every notice, reporting a UI preference as a
// finding. On 3418240003-000-02300 it said the values were identical while the notice stated 1,832,620 for
// school millage and 1,696,030 for non-school: a confident statement of the opposite of the document.
//
// ⛔ AND THE REAL ANSWER NEEDS NO NEW EXTRACTION. A TRIM's rate table carries each authority's OWN taxable
// value, so counting the distinct ones IS the detection. One group means the values genuinely are identical;
// two means the cap has bitten and a second column is needed.
//
// ⛔ VALUES AND COUNTS ONLY — NEVER WHICH IS "SCHOOL". That inference is a name heuristic off one county's
// print layout (docs/known-debt.md), and it is not needed: a TS reading the notice has its own labels in front
// of them. Stating the figures lets them match; guessing the label for them is where a wrong answer would hide.
export function noticeBases(lines) {
  const m = new Map();
  (Array.isArray(lines) ? lines : []).filter(Boolean).forEach((l) => {
    const v = n(l.taxable_value);
    if (v == null) return;
    const k = String(Math.round(v * 100) / 100);
    if (!m.has(k)) m.set(k, { value: v, authorities: [] });
    m.get(k).authorities.push(l.name || null);
  });
  // Largest first: where a cap applies, the uncapped (school) base is the larger, and a reader scanning the
  // sentence meets the bigger number first, as the notice itself prints it.
  const groups = [...m.values()].sort((a, b) => b.value - a.value);
  return { count: groups.length, groups, capDifference: groups.length === 2 ? Math.round((groups[0].value - groups[1].value) * 100) / 100 : null };
}

// ═══ SEED THE VALUE GRID'S COLUMNS FROM THE NOTICE'S OWN BASES ═══
//
// The scan always created ONE column called County and put the notice's taxable value in it. On a capped
// Florida parcel that value is the SCHOOL base — 34-18-24-0003-000-02400 filled 439,230 into a column labelled
// County when the county taxes 322,100 — so the form produced exactly the wrong-authority filing the banner
// then warns about. Two bases means two columns.
//
// ⛔ WHAT IS CERTAIN, AND WHAT IS INFERRED. Certain, needing no judgement: the SMALLER base is the reduced one
// (a reduction reduces), market value is the same in both columns (a cap or an exemption reduces ASSESSED
// value, not just value), and each column's taxable value is its own base.
//
// Inferred, but on STATUTE rather than on a print layout: the larger base is the SCHOOL base. Florida exempts
// school millage from the non-homestead cap, and the additional homestead exemption applies to NON-school
// millage only — so both of the reasons two bases arise push in the same direction, and the smaller base is
// non-school either way. That is categorically different from reading "SCHOOL" out of a line name, which is
// one county's print convention and stays out (docs/known-debt.md).
//
// ⛔ AND THE DIFFERENCE IS NOT WRITTEN AS "THE CAP" HERE, deliberately. A cap and an additional-homestead
// exemption produce the same shape, and only the notice's own reductions box distinguishes them. Filling
// Capped Limitation with an unverified difference would put a figure the parcel card SUBTRACTS behind a guess;
// that write waits for the stated cap to corroborate it.
//
// Refuses on anything it cannot ground: not Florida, not exactly two bases. One column then, and the banner's
// instruction to add one by hand stands.
export function planNoticeColumns(lines, opts = {}) {
  const { state = null } = opts;
  const bases = noticeBases(lines);
  const no = (reason) => ({ columns: null, difference: null, reason });
  if (bases.count !== 2) {
    return no(bases.count === 0 ? 'no per-authority taxable values on the notice'
      : bases.count === 1 ? 'one taxable base — a single column is correct'
      : `${bases.count} taxable bases, which is not the two-base shape this seeds`);
  }
  // ⛔ FLORIDA ONLY. The statutory ground for naming the larger base "Public Schools" is Florida's cap and
  // homestead rules. Elsewhere two bases may mean something else entirely, and a wrongly NAMED column is worse
  // than a missing one — LaSalle is all California, where one column must stay one column.
  if (String(state || '').toUpperCase() !== 'FL') {
    return no(`two taxable bases, but ${state ? `state is ${state}` : 'the state is unknown'} — the school/non-school split is a Florida rule`);
  }
  const [larger, smaller] = bases.groups;
  return {
    // County first, holding the REDUCED base: the convention is that County is the base column and a second,
    // named column appears only where a value actually differs.
    columns: [
      { name: 'County',         taxable: smaller.value, authorities: smaller.authorities.length },
      { name: 'Public Schools', taxable: larger.value,  authorities: larger.authorities.length },
    ],
    difference: bases.capDifference,
    reason: null,
  };
}

// ═══ THE NOTICE'S OWN TOTAL — THE ONLY WITNESS THAT CATCHES A DROPPED LINE ═══
//
// ⛔ EVERY OTHER CHECK IS BLIND TO ABSENCE. A dropped line removes its rate AND its tax, so Σ(rate) and
// Σ(tax)/base stay perfectly consistent with each other, and every surviving line still self-reconciles.
// Philip's hand arithmetic on 34-18-24-0003-000-02400 was wrong twice — once subtracting the wrong way, once
// dropping a line while adding — and the only thing that found it was the printed TOTAL AD VALOREM PROPERTY
// TAXES: 6,187.71 for the proposed scenario. Same lesson as check 7 on bills, where the derived column agreed
// with itself and only the model's independent read of the total disagreed.
//
// 5c tolerance, matching check 7: a dozen separately-rounded line taxes drift a cent or two from a separately
// rounded total, and the smallest real line is over a dollar.
const TOTAL_TOL = 0.05;

export function noticeTotalCheck(lines, statedTotal) {
  const stated = n(statedTotal);
  const list = (Array.isArray(lines) ? lines : []).filter(Boolean);
  const amounts = list.map((l) => n(l.amount)).filter((v) => v != null);
  if (stated == null) return { ok: null, sum: null, stated: null, delta: null, note: 'The notice stated no total, so a dropped line cannot be detected.' };
  if (!amounts.length) return { ok: null, sum: null, stated, delta: null, note: 'No line tax figures to total.' };
  const sum = Math.round(amounts.reduce((a, b) => a + b, 0) * 100) / 100;
  const delta = Math.round((sum - stated) * 100) / 100;
  if (Math.abs(delta) <= TOTAL_TOL) return { ok: true, sum, stated, delta, note: null };
  return {
    ok: false, sum, stated, delta,
    note: `The ${list.length} rate lines total ${sum.toFixed(2)} but the notice states ${stated.toFixed(2)}`
      + ` — ${delta < 0 ? `${Math.abs(delta).toFixed(2)} SHORT, so a line is probably missing` : `${delta.toFixed(2)} OVER, so a line is probably duplicated or misread`}.`,
  };
}

// ═══ IS THE DIFFERENCE BETWEEN THE BASES ACTUALLY THE CAP? ═══
//
// ⛔ A CAP AND AN ADDITIONAL HOMESTEAD EXEMPTION PRODUCE THE IDENTICAL SHAPE. Both reduce the NON-school base
// and leave the school base alone, so the difference between the two bases cannot be called "the cap" from
// arithmetic — only the notice's own reductions box names it. That is why column seeding does not fill the cap
// row: writing an unverified difference into a field the parcel card SUBTRACTS would put a figure behind a
// guess, and both causes look the same from here.
//
// Corroborated means: the stated figure equals the difference between the two bases. On
// 34-18-24-0003-000-02400 that is 439,230 − 322,100 = 117,130, the stated NON-HOMESTEAD 10% CAP exactly, and
// it held on the previous parcel too (1,832,620 − 1,696,030 = 136,590). A mismatch means a base or the cap was
// misread, and nothing is filled.
export function noticeCapCheck(lines, statedCap) {
  const stated = n(statedCap);
  const bases = noticeBases(lines);
  if (stated == null) return { ok: null, cap: null, stated: null, note: 'The notice stated no cap or limitation figure.' };
  if (bases.count !== 2) return { ok: null, cap: null, stated, note: `A cap of ${stated.toFixed(2)} was stated, but the notice shows ${bases.count} taxable base${bases.count === 1 ? '' : 's'} — nothing to check it against.` };
  const diff = bases.capDifference;
  if (Math.abs(diff - stated) <= 1) return { ok: true, cap: stated, stated, note: null };
  return {
    ok: false, cap: null, stated,
    note: `The notice states a reduction of ${stated.toFixed(2)} but its two taxable bases differ by ${diff.toFixed(2)}`
      + ` — one of the three figures was misread, so the cap is not filled in.`,
  };
}
